import React, { useContext, useEffect, useState } from 'react'
import { apiGet } from '../../utils/apiFetch'
import apiPath from '../../utils/pathObj'
import Pagination from '../Pagination'
import AuthContext from 'context/AuthContext'
import dayjs from 'dayjs'
import ODateRangePicker from 'components/shared/datePicker/ODateRangePicker'
import { useTranslation } from 'react-i18next'
import TotalAcceptedList from './TotalAcceptedList'
import TotalAcceptedView from './TotalAcceptedView'
import { useLocation, useNavigate } from 'react-router-dom'
import CustomSelect from 'components/reusable/CustomSelect'

function TotalAcceptedEmployee () {
  const { t } = useTranslation()
  const location = useLocation()
  const { logoutUser, user, updatePageName } = useContext(AuthContext)
  const navigate = useNavigate()
  const [paginationObj, setPaginationObj] = useState({
    page: 1,
    pageCount: 1,
    pageRangeDisplayed: 10
  })
  const [allTotalEmployee, setAllTotalEmployee] = useState([])
  const [acceptedShowModal, setAcceptedShowModal] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [item, setItem] = useState('')
  const [isDelete, setIsDelete] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [isInitialized, setIsInitialized] = useState(false)
  
  // Define sorting options
  const [sortOptions] = useState([
    { value: 'a_z', label: 'A-Z' },
    { value: 'z_a', label: 'Z-A' }
  ])
  
  // Define column options for sorting
  const [columnOptions] = useState([
    { value: 'employee_name', label: t('EMPLOYEE_NAME') },
    { value: 'job_title', label: t('JOB_TITLE') },
  ])

  const [filterData, setFilterData] = useState({
    category: '',
    searchkey: '',
    startDate: '',
    endDate: '',
    isReset: false,
    isFilter: false
  })
  
  // Updated sort state with sortColumn
  const [sort, setSort] = useState({
    sortBy: 'createdAt',
    sortType: 'desc',
    sortColumn: 'accepted_date'
  })

  const updateStateWithResult = result => {
    if (result?.status === 200) {
      const response = result?.data?.results
      const resultStatus = result?.data?.success
      setAllTotalEmployee(response?.docs)
      setPaginationObj({
        ...paginationObj,
        page: resultStatus ? response.page : null,
        pageCount: resultStatus ? response.totalPages : null,
        perPageItem: resultStatus ? response?.docs.length : null,
        totalItems: resultStatus ? response.totalDocs : null
      })
    }
  }

  // Map UI sort column to backend sortBy field
  const getSortByField = () => {
    switch (sort.sortColumn) {
      case 'employee_name':
        return 'Employee.name'
      case 'employee_id':
        return 'Employee.employeeId'
      case 'job_title':
        return 'Job.name'
      case 'department':
        return 'JobDepartment.name'
      case 'email':
        return 'Employee.email'
      case 'form_status':
        return 'formStatus'
      case 'accepted_date':
        return 'acceptedRejectedAt'
      default:
        return 'createdAt'
    }
  }

  const getTotalEmployee = async data => {
    try {
      const { category, startDate, endDate, searchkey, isFilter } = filterData
      if (
        (data?.deletePage && allTotalEmployee?.length >= 1) ||
        (isFilter && category && data?.status && allTotalEmployee?.length >= 1)
      ) {
        if (allTotalEmployee?.length >= 1) {
          setPage(page - 1)
          setIsDelete(true)
          setPaginationObj({ ...paginationObj, page: page - 1 })
        }
      } else {
        setIsDelete(false)
      }
      const payload = {
        page,
        pageSize,
        status: category,
        startDate: startDate ? dayjs(startDate).format('YYYY-MM-DD') : null,
        endDate: endDate ? dayjs(endDate).format('YYYY-MM-DD') : null,
        keyword: searchkey,
        sortBy: getSortByField(), // Use the mapped field
        sortType: sort.sortType
      }

      const path = apiPath.getTotalEmployee
      const result = await apiGet(path, payload)
      updateStateWithResult(result)
    } catch (error) {
      console.log('error ', error)
      setPaginationObj({})
      if (error.response.status === 401 || error.response.status === 409) {
        logoutUser()
      }
    }
  }

  const handlePageClick = event => {
    const newPage = event.selected + 1
    setPage(newPage)
  }

  const handleResetSate = () => {
    navigate('/all-employees', { state: null })
  }

  const statusPage = e => {
    if (location.state) {
      handleResetSate()
    }
    setFilterData({ ...filterData, category: e.target.value, isFilter: true })
    setPage(1)
  }

  useEffect(() => {
    if (!location.state) {
      getTotalEmployee()
    }
  }, [page, filterData, sort])

  const handleAcceptedView = items => {
    setItem(items)
    setAcceptedShowModal(true)
  }

  useEffect(() => {
    if (location.state)
      setFilterData({ ...filterData, category: location.state })
  }, [location.state])

  const handleReset = () => {
    if (location.state) {
      handleResetSate()
    }
    setFilterData({
      category: '',
      searchkey: '',
      startDate: '',
      endDate: '',
      isReset: true,
      isFilter: false
    })
    setSort({
      sortBy: 'createdAt',
      sortType: 'desc',
      sortColumn: 'accepted_date'
    })
    setPage(1)
    setIsDelete(true)
    setSearchTerm('')
  }

  const handleDateChange = (start, end) => {
    if (location.state) {
      handleResetSate()
    }
    setPage(1)
    setFilterData({
      ...filterData,
      startDate: start,
      endDate: end,
      isFilter: true
    })
    setIsDelete(true)
  }

  // Handle sort order change (A-Z/Z-A)
  const handleSortOrderChange = e => {
    const orderValue = e.target.value
    setSort(prev => ({
      ...prev,
      sortType: orderValue === 'a_z' ? 'asc' : 'desc'
    }))
    setPage(1)
  }

  // Handle sort column change
  const handleSortColumnChange = e => {
    const columnValue = e.target.value
    setSort(prev => ({
      ...prev,
      sortColumn: columnValue,
      // Reset to ascending when changing column
      sortType: 'asc'
    }))
    setPage(1)
  }

  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized(true)
    } else if (searchTerm || !filterData?.isReset) {
      setFilterData({
        ...filterData,
        isReset: false,
        searchkey: debouncedSearchTerm ? debouncedSearchTerm : '',
        isFilter: debouncedSearchTerm ? true : false
      })
      setPage(1)
    }
  }, [debouncedSearchTerm])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 500)
    return () => {
      clearTimeout(timeoutId)
    }
  }, [searchTerm])

  useEffect(() => {
    updatePageName(t('TOTAL_ACCEPTED_EMPLOYEES'))
  }, [])

  const handleSearchTerm = e => {
    if (location.state) {
      handleResetSate()
    }
    setSearchTerm(e.target.value)
  }

  return (
    <div>
      <div className='bg-[#F9F9F9]'>
        <div className='px-3 py-4'>
          <div className='bg-white border border-[#E9EDF9] rounded-lg'>
            <form className='border-b border-b-[#E3E3E3] 2xl:flex gap-2 px-4 py-3'>
              <div className='col-span-2 flex flex-wrap  items-center mb-2 2xl:mb-0'>
                <div className='flex items-center lg:pt-0 pt-3 flex-wrap '>
                  <div className='relative flex items-center mb-3'>
                    <div className='absolute inset-y-0 right-0 flex items-center pl-3 mr-3 pointer-events-none'>
                      {!searchTerm ? (
                        <svg
                          aria-hidden='true'
                          className='w-4 h-4 text-[#A5A5A5] dark:text-gray-40'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                          xmlns='http://www.w3.org/2000/svg'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth='2'
                            d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                          />
                        </svg>
                      ) : (
                        ''
                      )}
                    </div>
                    <input
                      type='search'
                      id='default-search'
                      className='block w-full p-2 outline-none text-sm text-gray-900 2xl:min-w-[250px] xl:min-w-[300px] rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500'
                      placeholder={t('SEARCH_BY_EMPLOYEE_NAME_AND_ID')}
                      value={searchTerm}
                      title=''
                      required
                      onChange={e => handleSearchTerm(e)}
                    />
                  </div>
                  <ODateRangePicker
                    handleDateChange={handleDateChange}
                    isReset={filterData?.isReset}
                    setIsReset={setFilterData}
                  />
                  <div className='flex items-center  ml-3 mb-3'>
                    <select
                      id=''
                      type='text '
                      name='category'
                      className='block p-2 w-full text-sm text-[#A5A5A5] bg-transparent border-2 rounded-lg border-[#DFDFDF]  dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0  peer'
                      placeholder=' '
                      onChange={e => statusPage(e)}
                      value={filterData?.category}
                    >
                      <option value=''>
                        {t('O_ALL')}
                      </option>
                      <option value='active'>{"Active"}</option>
                      <option value='inactive'>{"Inactive"}</option>
                      <option value='paid_leave'>{"Paid Leave"}</option>
                      <option value='unpaid_leave'>{"Unpaid Leave"}</option>
                      <option value='terminated'>{"Terminated"}</option>
                      <option value='not_on_payroll'>{"Not On Payroll"}</option>
                    </select>
                  </div>

                  {/* Sort Column Dropdown */}
                  <CustomSelect
                    id='sortColumn'
                    name='sortColumn'
                    options={columnOptions}
                    placeholder={t('SORT_BY_COLUMN')}
                    value={sort.sortColumn}
                    onChange={handleSortColumnChange}
                    t={t}
                    extraClasses='ml-3 mb-3'
                  />

                  {/* Sort Order Dropdown */}
                  <CustomSelect
                    id='sortOrder'
                    name='sortOrder'
                    options={sortOptions}
                    placeholder={t('SORT_ORDER')}
                    value={sort.sortType === 'asc' ? 'a_z' : 'z_a'}
                    onChange={handleSortOrderChange}
                    t={t}
                    extraClasses='ml-3 mb-3'
                  />

                  <button
                    type='button'
                    onClick={handleReset}
                    className='bg-LightBlue text-sm px-8 ml-3 mb-3 py-2 rounded-lg items-center border border-transparent text-white hover:bg-DarkBlue sm:w-auto w-1/2'
                  >
                    {t('O_RESET')}
                  </button>
                </div>
              </div>
            </form>
            <TotalAcceptedList
              allTotalEmployee={allTotalEmployee}
              user={user}
              getTotalEmployee={getTotalEmployee}
              handleAcceptedView={handleAcceptedView}
              page={page}
              setSort={setSort}
              sort={sort}
            />
            {paginationObj?.totalItems ? (
              <Pagination
                handlePageClick={handlePageClick}
                options={paginationObj}
                isDelete={isDelete}
                page={page}
              />
            ) : null}
          </div>
        </div>
      </div>
      <>
        {acceptedShowModal ? (
          <TotalAcceptedView
            setAcceptedShowModal={setAcceptedShowModal}
            getTotalEmployee={getTotalEmployee}
            item={item}
          />
        ) : null}
      </>
    </div>
  )
}
export default TotalAcceptedEmployee